import {
  Tooltip,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { InfoOutlineIcon } from '@chakra-ui/icons'
import { useQuery } from '@tanstack/react-query'
import { FiSend } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { monitoringService } from '@/services/admin/monitoringService'

const TOOLTIP_MODIFIERS = [
  {
    name: 'preventOverflow',
    options: {
      padding: 8,
      altAxis: true,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 8,
    },
  },
]

function formatMs(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '0 ms'
  }
  return `${Math.round(value)} ms`
}

type MetricLabelProps = {
  label: string
  help: string
  mutedText: string
}

function MetricLabel({ label, help, mutedText }: MetricLabelProps) {
  return (
    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
      <Text as="span">{label}</Text>
      <Tooltip
        label={help}
        hasArrow
        placement="top"
        modifiers={TOOLTIP_MODIFIERS}
        openDelay={200}
        pointerEvents="none"
        maxW="280px"
        whiteSpace="normal"
        textAlign="left"
      >
        <Box
          as="span"
          color={mutedText}
          cursor="help"
          aria-label={help}
          display="inline-flex"
          alignItems="center"
          lineHeight={1}
          flexShrink={0}
        >
          <InfoOutlineIcon boxSize={3.5} />
        </Box>
      </Tooltip>
    </Box>
  )
}

type SectionHelpProps = {
  help: string
  mutedText: string
}

function SectionHelp({ help, mutedText }: SectionHelpProps) {
  return (
    <Tooltip
      label={help}
      hasArrow
      placement="top"
      modifiers={TOOLTIP_MODIFIERS}
      openDelay={200}
      pointerEvents="none"
      maxW="280px"
      whiteSpace="normal"
      textAlign="left"
    >
      <Box
        as="span"
        color={mutedText}
        cursor="help"
        aria-label={help}
        display="inline-flex"
        alignItems="center"
        lineHeight={1}
        flexShrink={0}
      >
        <InfoOutlineIcon boxSize={4} />
      </Box>
    </Tooltip>
  )
}

export function WebhookMonitor() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const cardBg = useColorModeValue('white', 'gray.700')
  const mutedText = useColorModeValue('gray.600', 'gray.300')

  const { data, isLoading } = useQuery({
    queryKey: ['monitoring', 'metrics', currentEnv],
    queryFn: () => monitoringService.getMetrics(currentEnv),
    refetchInterval: 30 * 1000,
    staleTime: 15 * 1000,
  })
  const isUnavailable = !isLoading && !data

  const pendingCount = data?.webhook?.pendingCount ?? data?.performance?.webhookPendingCount ?? 0
  const retryScheduledCount =
    data?.webhook?.retryScheduledCount
    ?? data?.performance?.webhookRetryScheduledCount
    ?? 0
  const avgQueueLagMs =
    data?.webhook?.avgQueueLagMs
    ?? data?.performance?.webhookAvgQueueLagMs
    ?? 0
  const avgDeliveryMs =
    data?.webhook?.avgDeliveryMs
    ?? data?.performance?.webhookAvgDeliveryMs
    ?? 0
  const buckets = data?.webhook?.statusBuckets ?? {}
  const diagnostics = data?.webhook?.diagnostics
  const activeMerchantsTotal = diagnostics?.activeMerchantsTotal ?? 0
  const configuredMerchantsCount = diagnostics?.configuredMerchantsCount ?? 0
  const missingSettingsCount = diagnostics?.missingSettingsCount ?? 0
  const eventsCreatedLast24h = diagnostics?.eventsCreatedLast24h ?? 0
  const missingWebhookUrlCount = diagnostics?.missingWebhookUrlCount ?? 0
  const missingWebhookSecretCount = diagnostics?.missingWebhookSecretCount ?? 0

  return (
    <VStack spacing={4} align="stretch">
      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <HStack>
            <FiSend />
            <Heading size="md">{t('monitoring.webhook.title')}</Heading>
            <SectionHelp
              help={t('monitoring.webhook.subtitle')}
              mutedText={mutedText}
            />
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          {isUnavailable && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {t('monitoring.webhook.metricsUnavailable')}
              </AlertDescription>
            </Alert>
          )}
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.webhook.pendingCount')}
                    help={t('monitoring.webhook.help.pendingCount')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber fontSize="xl">
                  {isLoading || isUnavailable ? '--' : pendingCount}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.webhook.retryScheduledCount')}
                    help={t('monitoring.webhook.help.retryScheduledCount')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber fontSize="xl">
                  {isLoading || isUnavailable ? '--' : retryScheduledCount}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.webhook.avgQueueLagMs')}
                    help={t('monitoring.webhook.help.avgQueueLagMs')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber fontSize="xl">
                  {isLoading || isUnavailable ? '--' : formatMs(avgQueueLagMs)}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.webhook.avgDeliveryMs')}
                    help={t('monitoring.webhook.help.avgDeliveryMs')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber fontSize="xl">
                  {isLoading || isUnavailable ? '--' : formatMs(avgDeliveryMs)}
                </StatNumber>
              </Stat>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <Heading size="sm">{t('monitoring.webhook.statusBuckets')}</Heading>
        </CardHeader>
        <CardBody pt={1}>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.bucket2xx')}
                  help={t('monitoring.webhook.help.bucket2xx')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>{isLoading || isUnavailable ? '--' : buckets.s2xx ?? 0}</Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.bucket4xx')}
                  help={t('monitoring.webhook.help.bucket4xx')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>{isLoading || isUnavailable ? '--' : buckets.s4xx ?? 0}</Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.bucket5xx')}
                  help={t('monitoring.webhook.help.bucket5xx')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>{isLoading || isUnavailable ? '--' : buckets.s5xx ?? 0}</Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.bucketTimeoutNetwork')}
                  help={t('monitoring.webhook.help.bucketTimeoutNetwork')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading || isUnavailable ? '--' : buckets.timeoutOrNetwork ?? 0}
              </Heading>
            </Box>
          </Grid>
        </CardBody>
      </Card>

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <Heading size="sm">{t('monitoring.webhook.diagnosticsTitle')}</Heading>
        </CardHeader>
        <CardBody pt={1}>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.eventsCreatedLast24h')}
                  help={t('monitoring.webhook.help.eventsCreatedLast24h')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading || isUnavailable ? '--' : eventsCreatedLast24h}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.configuredMerchants')}
                  help={t('monitoring.webhook.help.configuredMerchants')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading || isUnavailable
                  ? '--'
                  : `${configuredMerchantsCount} / ${activeMerchantsTotal}`}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.missingWebhookUrl')}
                  help={t('monitoring.webhook.help.missingWebhookUrl')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading || isUnavailable ? '--' : missingWebhookUrlCount}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.webhook.missingWebhookSecret')}
                  help={t('monitoring.webhook.help.missingWebhookSecret')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading || isUnavailable ? '--' : missingWebhookSecretCount}
              </Heading>
            </Box>
          </Grid>
          {!isLoading && !isUnavailable && missingSettingsCount > 0 && (
            <Text color={mutedText} fontSize="sm" mt={4}>
              {t('monitoring.webhook.missingSettingsRow', { count: missingSettingsCount })}
            </Text>
          )}
        </CardBody>
      </Card>
    </VStack>
  )
}

export default WebhookMonitor
